import {
  CHATS_ROUTE,
  TMS_SIDEKICK_ROUTE,
} from 'clerk_common/constants/urls/routes';
import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { FetchedDomContent } from '@shared/contexts/ExtensionMessagingContext';
import { useMeContext } from '@shared/contexts/hooks/useMeContext';
import { useExtensionMessagingContext } from '@shared/contexts/useExtensionMessagingContext';
import { hasChatsAccess } from '@shared/utils/permissions';
import { PageInfoContextProvider } from './PageInfoContextProvider';
import { getTMSPageInfo, matchTMS } from './tms';
import { isGmailUrl, PageInfo, PageType, UnknownPageInfo } from './types';
import { getVoomaPageInfo, matchVooma } from './vooma';

const BASE_PATH = '/';

// NOTE(Martín): Now that we have a UNSUPPORTED_URL message, we can remove most of this
// as soon as all extensions are updated to use it

type SidekickNavigatorProps = {
  children: React.ReactNode;
};
export function SidekickNavigator({ children }: SidekickNavigatorProps) {
  const [pageInfo, setPageInfo] = useState<PageInfo | null>(null);
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { hasVee, isVoomaAdmin } = useMeContext();
  const { parentUrl, domIframe, dom } = useExtensionMessagingContext();
  const navigatedPath = useRef<string | null>(null);

  useEffect(() => {
    if (!parentUrl) return;
    const pageInfo = getPageInfo({ parentUrl, domIframe, dom });

    const path = determinePath({
      pageType: pageInfo.pageType,
      shouldDefaultToChats:
        !isGmailUrl(parentUrl) && hasChatsAccess({ hasVee, isVoomaAdmin }),
    });
    // NOTE(wassim): DOM snapshots arrive continuously; navigating on each one
    // remounts whatever route is open and destroys its state.
    if (navigatedPath.current !== path) {
      navigatedPath.current = path;
      if (pathname !== path) navigate(path);
    }
    setPageInfo(pageInfo);
  }, [dom, domIframe, hasVee, isVoomaAdmin, navigate, parentUrl, pathname]);

  return (
    <PageInfoContextProvider pageInfo={pageInfo}>
      {children}
    </PageInfoContextProvider>
  );
}

type GetPageInfoParams = {
  parentUrl: string;
  domIframe: FetchedDomContent | null;
  dom: FetchedDomContent | null;
};

function getPageInfo({
  parentUrl,
  domIframe,
  dom,
}: GetPageInfoParams): PageInfo {
  const tms = matchTMS(parentUrl);
  if (tms)
    return getTMSPageInfo(
      tms,
      parentUrl,
      domIframe?.domContent ?? '',
      dom?.domContent ?? ''
    );
  const voomaEnvironment = matchVooma(parentUrl);
  if (voomaEnvironment) return getVoomaPageInfo(voomaEnvironment);
  return getUnknownPageInfo();
}

function getUnknownPageInfo(): UnknownPageInfo {
  return { pageType: 'unknown' };
}

function determinePath({
  pageType,
  shouldDefaultToChats,
}: {
  pageType: PageType;
  shouldDefaultToChats: boolean;
}) {
  switch (pageType) {
    case 'tms':
      return `${TMS_SIDEKICK_ROUTE}/`;
    default:
      return shouldDefaultToChats ? CHATS_ROUTE : BASE_PATH;
  }
}
